import { notifications } from '@mantine/notifications';
import { useApi } from 'api/api-context';
import { GetCurrentUserResponse } from 'api/actions/get-current-user/get-current-user-response';
import { GetUserTemporaryPinCodesResponse } from 'api/actions/get-user-temporary-pin-codes/get-user-temporary-pin-codes-response';
import useLang from 'lang';
import SimpleLayout from 'layouts/SimpleLayout';
import panic from 'panic';
import React, { useEffect, useState } from 'react';
import TemporaryPinCodePresenter, {
  TemporaryPinCodeViewModel,
} from 'pages/house/temporary-pin-code/TemporaryPinCodePresenter';
import { HOUSE_PAGE_PATH, USERS_PAGE_PATH } from 'routes/paths';
import { Navigate, useParams } from 'react-router-dom';

type TemporaryPinCodeResponseItem = GetUserTemporaryPinCodesResponse['temporaryPinCodes'][number];

/**
 * Converts a temporary PIN API object into a stable UI view model.
 */
function normalizeTemporaryPinCode(temporaryPinCode: TemporaryPinCodeResponseItem): TemporaryPinCodeViewModel {
  return {
    id: temporaryPinCode.id || 0,
    pinCode: temporaryPinCode.pinCode || '',
    title: temporaryPinCode.title || null,
    validFrom: temporaryPinCode.validFrom || '',
    validTo: temporaryPinCode.validTo || '',
    syncFrom: temporaryPinCode.syncFrom || '',
    syncUntil: temporaryPinCode.syncUntil || '',
    canceledAt: temporaryPinCode.canceledAt || null,
  };
}

/**
 * API-backed adapter for the temporary PIN code page.
 */
export default function TemporaryPinCodeApiAdapter() {
  const { _t } = useLang();
  const { getAction, buildingId } = useApi();
  const { userId } = useParams<{ userId?: string }>();
  const [loading, setLoading] = useState(true);
  const [creatingTemporaryPin, setCreatingTemporaryPin] = useState(false);
  const [cancelingTemporaryPinId, setCancelingTemporaryPinId] = useState<number | null>(null);
  const [pinCodeEnabled, setPinCodeEnabled] = useState<boolean | null>(null);
  const [temporaryPinCodes, setTemporaryPinCodes] = useState<TemporaryPinCodeViewModel[]>([]);
  const [temporaryPinModalOpened, setTemporaryPinModalOpened] = useState(false);
  const [temporaryPinTitle, setTemporaryPinTitle] = useState('');
  const [temporaryPinValidFrom, setTemporaryPinValidFrom] = useState<Date | null>(new Date());
  const [temporaryPinValidTo, setTemporaryPinValidTo] = useState<Date | null>(new Date(Date.now() + 60 * 60 * 1000));

  const familyMemberId = userId ? Number(userId) : null;
  const isFamilyMemberPin = familyMemberId !== null && !Number.isNaN(familyMemberId);

  /**
   * Loads the current building feature configuration.
   */
  const loadPinCodeAvailability = () => {
    return getAction('GetCurrentUser')({}).then((response) => {
      const buildings = response.buildings || [];
      const currentBuilding = buildings.find(
        (building): building is NonNullable<GetCurrentUserResponse['buildings']>[0] => building.id === buildingId
      );

      return currentBuilding?.showPinCode === true;
    });
  };

  /**
   * Loads temporary PIN codes from the active backend endpoint.
   */
  const loadTemporaryPinCodes = () => {
    setLoading(true);

    loadPinCodeAvailability()
      .then((enabled) => {
        setPinCodeEnabled(enabled);

        if (!enabled) {
          setTemporaryPinCodes([]);
          return null;
        }

        const request = isFamilyMemberPin
          ? getAction('GetFamilyMemberTemporaryPinCodes')({ parameters: { userId: familyMemberId } })
          : getAction('GetUserTemporaryPinCodes')({});

        return request.then((response) => {
          setTemporaryPinCodes((response.temporaryPinCodes || []).map(normalizeTemporaryPinCode));
        });
      })
      .catch(panic)
      .finally(() => setLoading(false));
  };

  /**
   * Creates a new temporary PIN code for the active user endpoint.
   */
  const handleCreateTemporaryPin = () => {
    if (!temporaryPinValidFrom || !temporaryPinValidTo) {
      notifications.show({ color: 'red', message: _t('Select both the start and end time.') });
      return;
    }

    if (temporaryPinValidFrom >= temporaryPinValidTo) {
      notifications.show({ color: 'red', message: _t('The end time must be after the start time.') });
      return;
    }

    const normalizedTitle = temporaryPinTitle.trim();

    if (normalizedTitle.length > 100) {
      notifications.show({ color: 'red', message: _t('The title must be at most 100 characters long.') });
      return;
    }

    setCreatingTemporaryPin(true);

    const payload = {
      ...(normalizedTitle ? { title: normalizedTitle } : {}),
      validFrom: temporaryPinValidFrom.toISOString(),
      validTo: temporaryPinValidTo.toISOString(),
    };

    const request = isFamilyMemberPin
      ? getAction('CreateFamilyMemberTemporaryPinCode')({ parameters: { userId: familyMemberId }, payload })
      : getAction('CreateUserTemporaryPinCode')({ payload });

    request
      .then(() => {
        notifications.show({ color: 'green', message: _t('Temporary PIN code created.') });
        setTemporaryPinTitle('');
        setTemporaryPinModalOpened(false);
        loadTemporaryPinCodes();
      })
      .catch((error) => panic(error instanceof Error ? error : new Error(String(error))))
      .finally(() => setCreatingTemporaryPin(false));
  };

  /**
   * Cancels a temporary PIN code for the active user endpoint.
   */
  const handleCancelTemporaryPin = (temporaryPinCodeId: number) => {
    setCancelingTemporaryPinId(temporaryPinCodeId);

    const request = isFamilyMemberPin
      ? getAction('CancelFamilyMemberTemporaryPinCode')({
          parameters: { userId: familyMemberId, temporaryPinCodeId },
        })
      : getAction('CancelUserTemporaryPinCode')({ parameters: { temporaryPinCodeId } });

    request
      .then(() => {
        notifications.show({ color: 'green', message: _t('Temporary PIN code canceled.') });
        loadTemporaryPinCodes();
      })
      .catch((error) => panic(error instanceof Error ? error : new Error(String(error))))
      .finally(() => setCancelingTemporaryPinId(null));
  };

  useEffect(() => {
    loadTemporaryPinCodes();
  }, [buildingId, familyMemberId, getAction, isFamilyMemberPin]);

  if (pinCodeEnabled === false) {
    return <Navigate to={isFamilyMemberPin ? USERS_PAGE_PATH.original : HOUSE_PAGE_PATH.original} replace />;
  }

  return (
    <SimpleLayout>
      <TemporaryPinCodePresenter
        backPath={isFamilyMemberPin ? USERS_PAGE_PATH.original : HOUSE_PAGE_PATH.original}
        backLabel={isFamilyMemberPin ? _t('List of users') : _t('Back to my household')}
        loading={loading}
        creatingTemporaryPin={creatingTemporaryPin}
        cancelingTemporaryPinId={cancelingTemporaryPinId}
        temporaryPinCodes={temporaryPinCodes}
        temporaryPinModalOpened={temporaryPinModalOpened}
        temporaryPinTitle={temporaryPinTitle}
        temporaryPinValidFrom={temporaryPinValidFrom}
        temporaryPinValidTo={temporaryPinValidTo}
        onOpenTemporaryPinModal={() => setTemporaryPinModalOpened(true)}
        onCloseTemporaryPinModal={() => {
          setTemporaryPinTitle('');
          setTemporaryPinModalOpened(false);
        }}
        onTemporaryPinTitleChange={setTemporaryPinTitle}
        onTemporaryPinValidFromChange={setTemporaryPinValidFrom}
        onTemporaryPinValidToChange={setTemporaryPinValidTo}
        onCreateTemporaryPin={handleCreateTemporaryPin}
        onCancelTemporaryPin={handleCancelTemporaryPin}
        _t={_t}
      />
    </SimpleLayout>
  );
}
